'use client'

import { useIsAnotherNiceModalOpened } from '@advanza/hooks/useIsAnotherNiceModalOpened'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'

const buttonClass = 'rounded-md bg-primary-500 px-4 py-2 text-md font-medium text-white'

const TestNestedBaseModal = NiceModal.create(() => {
    const modal = useModal()

    return (
        <BaseModal overlayId="test-nested-base-modal" maxWidth={360} closeOnOutSideClick>
            <BaseModalHeader>
                <h3 className="text-xl font-semibold text-text-primary-900">Nested BaseModal</h3>
            </BaseModalHeader>
            <BaseModalMainContent>
                <p className="text-md text-text-primary-900">
                    I am a second BaseModal stacked on top of the parent. Close me (button,
                    backdrop, Escape or browser Back) — the parent must stay open.
                </p>
            </BaseModalMainContent>
            <BaseModalFooter className="text-right">
                <button type="button" onClick={() => modal.hide()} className={buttonClass}>
                    Close nested modal
                </button>
            </BaseModalFooter>
        </BaseModal>
    )
})

/**
 * App-Router-native `BaseModal` demo (dev only).
 *
 * Exercises the new `@trustoo/components/modal/BaseModal` in a stacked scenario:
 * 1. Modals shown from App-Router-native code render with full context via the single provider.
 * 2. The parent refuses to close while the nested modal is open — the `useIsAnotherNiceModalOpened`
 *    guard on `closeOnOutSideClick` (mirrors the real EditRequestModal).
 * 3. Back-button close is baked into `BaseModal` (via `overlayId`), so neither modal wires
 *    `useCloseOnBack` manually — closing the nested one via browser Back leaves the parent open.
 */
const TestBaseModal = NiceModal.create(() => {
    const isAnotherModalOpened = useIsAnotherNiceModalOpened()

    return (
        <BaseModal
            overlayId="test-base-modal"
            maxWidth={440}
            closeOnOutSideClick={!isAnotherModalOpened}>
            <BaseModalHeader>
                <h3 className="text-xl font-semibold text-text-primary-900">Parent BaseModal</h3>
            </BaseModalHeader>
            <BaseModalMainContent>
                <p className="text-md text-text-primary-900">
                    Another modal open:{' '}
                    <b>{isAnotherModalOpened ? 'yes — I will not close' : 'no'}</b>
                </p>
                <p className="mt-2 text-md text-text-primary-900">
                    Open the nested modal, then close it: this parent should remain open the whole
                    time.
                </p>
            </BaseModalMainContent>
            <BaseModalFooter className="text-right">
                <button
                    type="button"
                    onClick={() => NiceModal.show(TestNestedBaseModal)}
                    className={buttonClass}>
                    Open nested modal
                </button>
            </BaseModalFooter>
        </BaseModal>
    )
})

const TestBaseModalPage = () => {
    return (
        <div className="flex flex-col items-center justify-center p-4">
            <div className="mb-4">
                <h1>BaseModal testing guide</h1>
                <ul>
                    <li>
                        Open, close with X / Escape / backdrop — modal closes, URL unchanged, and
                        the page's own nav still works afterwards.
                    </li>
                    <li>Open, press Back — modal closes, page stays.</li>
                    <li>
                        Open, close with X, then press Back — you should leave the page in one press
                        (that's the pass-on chain).
                    </li>
                </ul>
            </div>

            <button
                type="button"
                onClick={() => NiceModal.show(TestBaseModal)}
                className="rounded-md bg-primary-500 px-4 py-2 text-md font-medium text-white">
                Show nested-modal demo (BaseModal)
            </button>
        </div>
    )
}

export default TestBaseModalPage
